'use client';
/**
 * Recherche transversale du module Qualité.
 *
 * Elle n'interroge PAS le serveur : toutes les collections sont déjà chargées
 * dans le magasin par `refresh()`. Chercher revient donc à filtrer ce que la
 * session a déjà le droit de lire — le périmètre de la recherche est celui des
 * droits, sans qu'aucune règle ait à être réécrite ici.
 *
 * ⚠️ Données de santé : les motifs et justificatifs d'absence ne sont JAMAIS
 * indexés, même pour le rôle `rh` qui y a pourtant accès dans son écran. Un
 * résultat de recherche s'affiche souvent devant témoin, et le cloisonnement du
 * module RH ne doit pas se contourner par une barre de recherche.
 */
import React from 'react';
import { useGed, userName, type CleSelection } from '@/lib/store';
import { VOILE, COUCHES, card, I, inputStyle } from '@/components/ui';
import { SECTIONS } from '@/lib/onglets-parametrage';
import { ACCUEIL, famillesVisibles, droitsNav, type DroitsNav } from '@/lib/navigation';
import type { GedView } from '@/lib/types';
import { moteur } from '@/components/screens/manuel/Registres';

/** Repli sur la casse et les accents : « hemostase » doit trouver « Hémostase ». */
const norm = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

type Resultat = {
  id: string;
  /** Famille affichée en tête de groupe. */
  famille: string;
  /** Ce qui identifie l'objet — référence, code, nom. */
  titre: string;
  /** Le contexte qui permet de reconnaître le bon parmi plusieurs homonymes. */
  detail: string;
  /** Texte réellement fouillé (déjà normalisé). */
  index: string;
  ouvrir: () => void;
};

/** Ordre des familles : ce qu'on cherche le plus souvent d'abord.
 *
 *  « Écrans » vient EN TÊTE : quand on tape « congés » ou « CQI », on cherche
 *  presque toujours l'endroit où aller, pas une fiche précise. Les enterrer
 *  sous douze références de documents aurait rendu la palette inutile comme
 *  moyen de navigation — ce qu'elle est devenue depuis que la barre latérale
 *  compte plus de quarante entrées. */
const ORDRE = [
  'Écrans',
  // Le RÉFÉRENTIEL haut dans la liste (retour Ines du 19/08) : « GLY »,
  // « NFS », « hémostase » se cherchent au moins aussi souvent qu'un document,
  // et ces trois listes sont le cœur du catalogue.
  'Examens', 'Paramètres', 'Techniques',
  'Documents', 'Non-conformités', 'Actions correctives', 'Réclamations',
  'Audits', 'Revues de direction', 'Équipements', 'Réactifs & lots',
  'Fournisseurs', 'Contrôles internes', 'Évaluations externes', 'Personnel',
  // En DERNIER : on cherche d'abord une fiche, un document, un écran. Le
  // réglage est ce qu'on cherche quand on n'a pas trouvé le reste.
  'Paramétrage',
];

/**
 * LE RACCOURCI, ÉCRIT DANS LA LANGUE DE LA MACHINE (relevé du 27/08/2026, P0.7).
 *
 * Le badge affichait « Ctrl K » partout, macOS compris — où le raccourci est
 * ⌘K, et où Ctrl+K est pris par le navigateur (« rechercher depuis la barre
 * d'adresse »). Une personne sur Mac lisait donc une consigne qui, appliquée à
 * la lettre, ouvrait autre chose. Le gestionnaire, lui, acceptait déjà les deux
 * touches — seul l'affichage mentait.
 *
 * ⚠️ Calculé À L'EXÉCUTION et non au rendu du serveur : `navigator` n'existe pas
 * pendant le rendu serveur, et une valeur figée à la construction serait celle
 * de la machine qui construit l'image. Le repli « Ctrl » est le cas majoritaire
 * au laboratoire.
 */
const estMac = typeof navigator !== 'undefined'
  && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent || '');
const RACCOURCI = estMac ? '⌘ K' : 'Ctrl K';

export function RecherchePalette() {
  const {
    docs, ncs, actions, recls, audits, rdds, equips, lots, cartesCqi, eeqs,
    settings, session, rhAcces, naviguerVers, openDoc, openSettings,
  } = useGed();

  const [ouvert, setOuvert] = React.useState(false);

  /* Le RÉFÉRENTIEL des analyses n'est pas dans le magasin : il vit derrière ses
     propres routes (`manuel-ref/*`), et l'écran Qualité les charge lui-même.
     La palette les lit donc UNE FOIS, à la première ouverture, puis garde le
     résultat pour la session — trois listes de quelques centaines de lignes,
     que l'on ne va pas rappeler à chaque frappe.

     Ce n'est pas une entorse à la règle « on ne cherche que ce qui est déjà
     chargé » : ces trois routes servent le catalogue, lisible par tout agent
     connecté, exactement comme la vitrine publique du manuel. Aucune donnée
     n'y est filtrée par rôle, il n'y a donc pas de périmètre à contourner. */
  const [ref, setRef] = React.useState<{
    examens: { documentId: string; code: string; libelle: string; famille: string | null;
      archivee: boolean; analytes: { code: string; nom: string }[] }[];
    parametres: { code: string; nom: string; discipline: string | null; matiere: string | null;
      uniteDefaut: string | null; alias: unknown }[];
    techniques: { code: string; nom: string; actif: boolean;
      fiches: { code: string }[]; equipements: { code: string; nom: string }[] }[];
  } | null>(null);
  const refDemande = React.useRef(false);
  React.useEffect(() => {
    if (!ouvert || refDemande.current) return;
    refDemande.current = true;
    let vivant = true;
    void Promise.all([
      moteur('manuel-ref/catalogue'), moteur('manuel-ref/parametres'), moteur('manuel-ref/techniques'),
    ]).then(([c, p, t]) => {
      if (!vivant) return;
      setRef({
        examens: (c.ok ? (c.data.fiches as never[]) : []) || [],
        parametres: (p.ok ? (p.data.parametres as never[]) : []) || [],
        techniques: (t.ok ? (t.data.techniques as never[]) : []) || [],
      });
      /* Un échec ne se signale pas ici : la palette reste utilisable pour tout
         le reste, et une erreur rouge au-dessus des résultats pour une liste
         annexe ferait plus de bruit que de service. Il se REJOUE en revanche à
         la prochaine ouverture, faute de quoi un incident réseau passager
         priverait le reste de la session du référentiel. */
      if (!c.ok || !p.ok || !t.ok) refDemande.current = false;
    });
    return () => { vivant = false; };
  }, [ouvert]);
  const [q, setQ] = React.useState('');
  const [curseur, setCurseur] = React.useState(0);
  const champRef = React.useRef<HTMLInputElement | null>(null);

  // Ctrl/Cmd+K ouvre, Échap ferme. Le raccourci est intercepté avant le
  // navigateur, qui l'affecte à sa propre barre d'adresse sur certains systèmes.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOuvert((v) => !v);
        return;
      }
      if (e.key === 'Escape' && ouvert) { e.stopPropagation(); setOuvert(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ouvert]);

  // Le champ reçoit le focus quand la palette s'ouvre. La remise à zéro de la
  // recherche, elle, se fait AU GESTE d'ouverture (voir `ouvrir`) : la poser ici
  // reviendrait à appeler setState en tête d'effet, donc à provoquer un second
  // rendu immédiat à chaque ouverture.
  React.useEffect(() => {
    if (!ouvert) return undefined;
    const t = window.setTimeout(() => champRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [ouvert]);

  // Un seul appel `naviguerVers` : il désigne l'objet EN MÊME TEMPS qu'il
  // change d'écran, là où `select` puis `go` empilait l'historique dans le
  // mauvais ordre (ancien écran + nouvelle sélection).
  const naviguer = React.useCallback((vue: GedView, cle?: CleSelection, id?: string) => {
    naviguerVers(vue, cle && id ? { cle, valeur: id } : undefined);
    setOuvert(false);
  }, [naviguerVers]);

  const tout: Resultat[] = React.useMemo(() => {
    if (!ouvert) return [];
    const r: Resultat[] = [];

    // Les ÉCRANS eux-mêmes : sauter directement à une page par son nom, ou par
    // le mot qu'on emploie pour elle. Le périmètre est exactement celui de la
    // barre latérale — `famillesVisibles` applique les mêmes conditions de
    // profil, si bien qu'un écran masqué dans la barre reste introuvable ici.
    const droits: DroitsNav = droitsNav(session, !!rhAcces?.acces);
    const ecrans = [
      { entree: ACCUEIL, famille: 'Module Qualité' },
      ...famillesVisibles(droits).flatMap((f) => f.entrees.map((entree) => ({ entree, famille: f.titre }))),
    ];
    for (const { entree, famille } of ecrans) {
      r.push({
        id: `vue-${entree.view}`, famille: 'Écrans', titre: entree.label, detail: famille,
        index: norm(`${entree.label} ${entree.synonymes || ''} ${famille}`),
        ouvrir: () => naviguer(entree.view),
      });
    }

    /* Les ONGLETS DE PARAMÉTRAGE (retour Ines du 18/08). On ne cherchait que
       dans les écrans et les fiches ; « où règle-t-on les jours fériés ? » se
       répondait en ouvrant la fenêtre et en parcourant six groupes.
       Le filtre de rôle est le MÊME que celui de l'écran : un onglet fermé à
       l'utilisateur reste introuvable ici, comme les écrans de la barre. */
    const estRQ = session.roles.includes('rq');
    const estRH = session.roles.includes('rh');
    for (const sec of SECTIONS) {
      if (sec.role === 'rq' ? !estRQ : !estRH) continue;
      for (const it of sec.items) {
        r.push({
          id: `param-${it.tab}`,
          famille: 'Paramétrage',
          titre: it.label,
          detail: sec.group,
          index: norm(`${it.label} ${sec.group} paramétrage réglage réglages configuration`),
          ouvrir: () => { openSettings(it.tab); setOuvert(false); },
        });
      }
    }

    /* Le référentiel des analyses : examens, paramètres, techniques.
       Chacun ouvre l'écran SUR SA LISTE et sur la bonne fiche — désigner
       « GLY » pour retomber sur un catalogue de trois cents lignes n'aurait
       rendu aucun service. */
    /* Même règle que pour les écrans : si le référentiel est fermé au profil,
       il reste introuvable ici. L'entrée n'a aujourd'hui aucune condition de
       rôle — le test est là pour que le jour où on en pose une, la palette la
       suive d'elle-même. */
    const refVisible = ecrans.some(({ entree }) => entree.view === 'manuel');
    for (const x of (refVisible && ref?.examens) || []) {
      if (x.archivee) continue;
      const parametres = (x.analytes || []).map((a) => a.code).join(' ');
      r.push({
        id: `ex-${x.documentId}`, famille: 'Examens', titre: x.code,
        detail: [x.libelle, x.famille].filter(Boolean).join(' · '),
        // Les CODES DES PARAMÈTRES sont indexés sur l'examen : on cherche
        // souvent la NFS en tapant « HB », qu'on lit sur le compte rendu.
        index: norm(`${x.code} ${x.libelle} ${x.famille || ''} ${parametres} examen analyse`),
        ouvrir: () => naviguer('manuel', 'selectedRef', `examen:${x.code}`),
      });
    }
    for (const p of (refVisible && ref?.parametres) || []) {
      const alias = Array.isArray(p.alias) ? p.alias.join(' ') : String(p.alias ?? '');
      r.push({
        id: `pa-${p.code}`, famille: 'Paramètres', titre: p.code,
        detail: [p.nom, p.discipline, p.matiere].filter(Boolean).join(' · '),
        index: norm(`${p.code} ${p.nom} ${p.discipline || ''} ${p.matiere || ''} ${p.uniteDefaut || ''} ${alias} paramètre analyte mesurande`),
        ouvrir: () => naviguer('manuel', 'selectedRef', `parametre:${p.code}`),
      });
    }
    for (const t of (refVisible && ref?.techniques) || []) {
      if (!t.actif) continue;
      r.push({
        id: `te-${t.code}`, famille: 'Techniques', titre: t.code,
        detail: [t.nom, (t.equipements || []).map((e) => e.nom).join(', ')].filter(Boolean).join(' · '),
        index: norm(`${t.code} ${t.nom} ${(t.equipements || []).map((e) => `${e.code} ${e.nom}`).join(' ')} ${(t.fiches || []).map((f) => f.code).join(' ')} technique méthode`),
        ouvrir: () => naviguer('manuel', 'selectedRef', `technique:${t.code}`),
      });
    }

    for (const d of docs) {
      r.push({
        id: `doc-${d.documentId}`, famille: 'Documents', titre: d.code, detail: d.titre,
        index: norm(`${d.code} ${d.titre} ${d.discipline} ${(d.motsCles || []).join(' ')} ${(d.tags || []).join(' ')}`),
        ouvrir: () => { openDoc(d.code); setOuvert(false); },
      });
    }
    for (const n of ncs) {
      r.push({
        id: `nc-${n.documentId}`, famille: 'Non-conformités', titre: n.ref, detail: n.titre,
        index: norm(`${n.ref} ${n.titre} ${n.processus} ${n.docLie}`),
        ouvrir: () => naviguer('nc', 'selectedNC', n.documentId),
      });
    }
    for (const a of actions) {
      r.push({
        id: `ac-${a.documentId}`, famille: 'Actions correctives', titre: a.ref, detail: a.titre,
        index: norm(`${a.ref} ${a.titre} ${a.origineRef} ${userName(settings, a.responsable)}`),
        ouvrir: () => naviguer('actions', 'selectedAction', a.documentId),
      });
    }
    for (const x of recls) {
      r.push({
        id: `rc-${x.documentId}`, famille: 'Réclamations', titre: x.ref, detail: x.objet,
        index: norm(`${x.ref} ${x.objet} ${x.plaignant}`),
        ouvrir: () => naviguer('recl', 'selectedRecl', x.documentId),
      });
    }
    for (const a of audits) {
      r.push({
        id: `au-${a.documentId}`, famille: 'Audits', titre: a.ref, detail: a.intitule,
        index: norm(`${a.ref} ${a.intitule} ${a.perimetre} ${userName(settings, a.auditeur)}`),
        ouvrir: () => naviguer('audits', 'selectedAudit', a.documentId),
      });
    }
    for (const x of rdds) {
      r.push({
        id: `rd-${x.documentId}`, famille: 'Revues de direction', titre: x.ref, detail: `Revue du ${x.dateRevue}`,
        index: norm(`${x.ref} ${x.dateRevue}`),
        ouvrir: () => naviguer('rdd', 'selectedRdd', x.documentId),
      });
    }
    for (const e of equips) {
      r.push({
        id: `eq-${e.documentId}`, famille: 'Équipements', titre: e.nom,
        // Le CODE d'inventaire manquait à l'index : « EQ03 » ne trouvait rien,
        // alors que c'est le nom que l'appareil porte sur son étiquette et
        // celui qui figure sur les relevés. On appelle un équipement par son
        // code OU par son nom — les deux mènent ici.
        detail: [e.code, e.marque, e.numeroSerie, e.localisation].filter(Boolean).join(' · '),
        index: norm(`${e.code || ''} ${e.nom} ${e.marque} ${e.numeroSerie} ${e.localisation} ${e.discipline}`),
        ouvrir: () => naviguer('equip', 'selectedEquip', e.documentId),
      });
    }
    for (const l of lots) {
      r.push({
        id: `lot-${l.documentId}`, famille: 'Réactifs & lots', titre: l.ref,
        detail: [l.designation, l.numeroLot, l.fournisseur].filter(Boolean).join(' · '),
        index: norm(`${l.ref} ${l.designation} ${l.numeroLot} ${l.fournisseur} ${l.discipline}`),
        ouvrir: () => naviguer('fourn'),
      });
    }
    for (const c of cartesCqi) {
      r.push({
        id: `cqi-${c.carteId}`, famille: 'Contrôles internes', titre: c.analyteNom,
        // On cherche aussi bien « ferritine » que « FERR » ou « EQ09 » : le code
        // analyte et le code équipement sont ceux qui figurent sur les relevés.
        detail: [c.equipement, c.equipementNom, c.discipline].filter(Boolean).join(' · '),
        index: norm(`${c.analyteNom} ${c.analyte} ${c.equipement} ${c.equipementNom} ${c.discipline}`),
        ouvrir: () => naviguer('cqi'),
      });
    }
    for (const e of eeqs) {
      r.push({
        id: `eeq-${e.documentId}`, famille: 'Évaluations externes', titre: e.analyte,
        detail: [e.programme, e.organisme].filter(Boolean).join(' · '),
        index: norm(`${e.analyte} ${e.programme} ${e.organisme} ${e.discipline}`),
        ouvrir: () => naviguer('eeq'),
      });
    }
    // Personnel : identité et fonction seulement. Rien de ce que le module RH
    // protège — ni motif d'absence, ni justificatif, ni élément de paie.
    for (const u of settings?.gedUsers || []) {
      r.push({
        id: `us-${u.id}`, famille: 'Personnel', titre: u.nom, detail: u.fonction || '',
        index: norm(`${u.nom} ${u.initiales} ${u.fonction}`),
        ouvrir: () => naviguer('org'),
      });
    }
    return r;
  }, [ouvert, ref, docs, ncs, actions, recls, audits, rdds, equips, lots, cartesCqi, eeqs, settings, session, rhAcces, naviguer, openDoc]);

  // Tous les termes doivent être présents : « cobas glyc » trouve le CQI
  // glycémie sur Cobas sans imposer l'ordre des mots.
  const termes = norm(q).split(/\s+/).filter(Boolean);
  const trouves = termes.length
    ? tout.filter((x) => termes.every((t) => x.index.includes(t)))
    : [];

  const groupes = ORDRE
    .map((famille) => ({ famille, items: trouves.filter((x) => x.famille === famille).slice(0, 6) }))
    .filter((g) => g.items.length);
  const plat = groupes.flatMap((g) => g.items);
  const actif = plat[Math.min(curseur, plat.length - 1)];

  if (!session.authenticated) return null;

  if (!ouvert) {
    return (
      <button onClick={() => setOuvert(true)} className="ged-btn" title={`Rechercher partout (${RACCOURCI})`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--surface-sunken)',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)',
          padding: '6px 12px', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5,
          color: 'var(--text-muted)',
        }}>
        <I n="eye" s={13} />Rechercher…
        <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, background: '#fff', border: '1px solid var(--border-default)', borderRadius: 4, padding: '1px 5px' }}>{RACCOURCI}</kbd>
      </button>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Rechercher dans le module Qualité"
      /* Fenêtre de CONSULTATION : le clic à côté referme, c'est le confort
         que la RQ a demandé de garder le 04/09/2026. Rien ne s'y saisit. */
      onClick={(e) => { if (e.target === e.currentTarget) setOuvert(false); }}
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '10vh 16px 16px' }}>
      <div style={{ ...card, width: '100%', maxWidth: 620, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
        <div className="ged-recherche-globale"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border-default)' }}>
          <I n="eye" s={16} style={{ color: 'var(--text-muted)' }} />
          <input
            ref={champRef} value={q}
            onChange={(e) => { setQ(e.target.value); setCurseur(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setCurseur((c) => Math.min(c + 1, plat.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setCurseur((c) => Math.max(c - 1, 0)); }
              else if (e.key === 'Enter' && actif) { e.preventDefault(); actif.ouvrir(); }
            }}
            placeholder="Un écran, une référence, un nom… (congés, CQI, BIO24-NC-2026-011, Cobas)"
            aria-label="Terme recherché"
            style={{ ...inputStyle, border: 'none', height: 30, padding: 0, fontSize: 14.5 }} />
          {/* Effacer sans fermer : on garde la palette ouverte et on repart
              d'une autre recherche. Échap, lui, ferme — c'est ce qu'annonce la
              touche affichée à côté, et deux sens pour une même touche
              obligeraient à deviner lequel s'applique. */}
          {q.length > 0 && (
            <button
              type="button"
              onClick={() => { setQ(''); setCurseur(0); champRef.current?.focus(); }}
              title="Effacer la recherche" aria-label="Effacer la recherche"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 22, height: 22, flex: '0 0 auto', background: 'transparent',
                border: 'none', borderRadius: '50%', color: 'var(--text-muted)',
                cursor: 'pointer', padding: 0,
              }}>
              <I n="x" s={14} />
            </button>
          )}
          <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-muted)' }}>Échap</kbd>
        </div>

        <div style={{ overflowY: 'auto', padding: groupes.length ? '6px 0' : 0 }}>
          {!termes.length && (
            <p style={{ padding: '22px 18px', margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Cherche dans tout le module Qualité : les <strong>écrans</strong> (tapez « congés », « CQI »,
              « métrologie »), puis les documents, non-conformités, actions, réclamations,
              audits, revues, équipements, réactifs, fournisseurs, contrôles et personnel.
              <br />
              <span style={{ color: 'var(--text-faint)' }}>
                Les motifs d’absence et les éléments de paie ne sont pas consultables ici.
              </span>
            </p>
          )}
          {!!termes.length && !plat.length && (
            <p style={{ padding: '22px 18px', margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Rien ne correspond à « {q.trim()} ».
            </p>
          )}
          {groupes.map((g) => (
            <div key={g.famille}>
              <div style={{ padding: '8px 16px 4px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                {g.famille}
              </div>
              {g.items.map((x) => {
                const surLigne = actif?.id === x.id;
                return (
                  <button key={x.id} onClick={x.ouvrir} onMouseEnter={() => setCurseur(plat.findIndex((p) => p.id === x.id))}
                    className="ged-btn"
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', textAlign: 'start',
                      background: surLigne ? 'var(--cyan-50)' : 'transparent', border: 'none',
                      padding: '7px 16px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                    }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', flex: 'none' }}>{x.titre}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.detail}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {!!plat.length && (
          <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '7px 16px', fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', gap: 14 }}>
            <span>↑ ↓ parcourir</span><span>Entrée ouvrir</span><span>Échap fermer</span>
          </div>
        )}
      </div>
    </div>
  );
}
